import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import "./TableSettings.css";
import { useParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { Replay } from "@mui/icons-material";
import { TableContext, TableContextType } from "../tableContext";
import GameSituationSetuper from "../GameSituationSetuper";
import { getComponentsImport, getSetupImport } from "../projectImports";
import { mergeDeep } from "../tableUtility";

export type TableSettingsProps = {
  socket: Socket;
  scaleAdjust: number;
  changeScaleAdjust: Function;
  globalVolume: number;
  changeGlobalVolume: Function;
  evalSpeed: number;
  changeEvalSpeed: Function;
  deckReloadStatus: string;
  setDeckReloadStatus: (newValue: string) => void;
  dataSheetReloadStatus: string;
  setDataSheetReloadStatus: (newValue: string) => void;
  editMode: boolean;
  setEditMode: (newValue: boolean) => void;
  edgeScroll: boolean;
  setEdgeScroll: (newValue: boolean) => void;
  cheatMode: boolean;
  setCheatMode: (newValue: boolean) => void;
  displayTooltips: boolean;
  setDisplayTooltips: (newValue: boolean) => void;
  projectRole?: string;
};

function TableSettings({
  socket,
  scaleAdjust,
  changeScaleAdjust,
  globalVolume,
  changeGlobalVolume,
  evalSpeed,
  changeEvalSpeed,
  deckReloadStatus,
  setDeckReloadStatus,
  dataSheetReloadStatus,
  setDataSheetReloadStatus,
  editMode,
  setEditMode,
  edgeScroll,
  setEdgeScroll,
  cheatMode,
  setCheatMode,
  displayTooltips,
  setDisplayTooltips,
  projectRole,
}: TableSettingsProps) {
  let params = useParams();
  const [isEditorBound, setIsEditorBound] = useState<boolean>(false);
  const [localScaleAdjust, setLocalScaleAdjust] = useState<number>(scaleAdjust);
  let { reloadComponents, tableState, tableId } = useContext(TableContext as React.Context<TableContextType>);
  useEffect(() => {
    const scaleAdjustTimeoutId = setTimeout(() => {
      changeScaleAdjust(localScaleAdjust);
    }, 250);
    return () => clearTimeout(scaleAdjustTimeoutId);
  }, [localScaleAdjust]);

  function reloadDecks() {
    setDeckReloadStatus("Reloading decks...");
    socket.emit("reload decks", JSON.stringify({ tableId: params.tableId }));
  }

  function reloadDataSheet() {
    setDataSheetReloadStatus("Reloading data sheet...");
    socket.emit("reload data sheet", JSON.stringify({ tableId: params.tableId }));
  }

  function reloadDevEditorComponents() {
    console.log("reloading dev components", params);
    socket.emit("reload dev editor components", JSON.stringify({ tableId: params.tableId }));
  }

  async function restartTable() {
    let gameSituationForSetup = GameSituationSetuper.createNewSituation(
      params.projectSlug ?? "",
      window.componentPool ?? {},
      tableState.options,
      tableState.deckData,
      tableState.dataSheet,
    );
    let setup = (await getSetupImport(params.projectSlug ?? "")).default;
    setup(gameSituationForSetup);
    let oldComponents = window.componentPool ?? {};
    let newComponents = {};
    await getComponentsImport(params.projectSlug ?? "")
      .then((module) => {
        newComponents = module.default;
        mergeDeep(oldComponents, newComponents ?? {});
        window.componentPool = newComponents;
      })
      .catch((err) => {
        console.log(err);
        throw new Error("Could not load components: " + err.message);
      });
    fetch("/api/projects/" + params.projectSlug + "/" + params.tableId + "/restart-table/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        start_position: gameSituationForSetup.getPositionData(),
        components: newComponents,
      }),
    }).then((response) => {
      console.log(response);
    });
  }

  const toggleBindTableToEditor = useCallback(async () => {
    if (isEditorBound) {
      fetch("/api/unbind-table/" + params.tableId + "/" + params.projectSlug, { method: "POST" }).then((response) => {
        console.log(response);
        if (response.ok) {
          setIsEditorBound(false);
        }
      });
    } else {
      fetch("/api/bind-table/" + params.tableId + "/" + params.projectSlug, { method: "POST" }).then((response) => {
        console.log(response);
        if (response.ok) {
          setIsEditorBound(true);
        }
      });
    }
  }, [isEditorBound]);

  function handleEditModeChanged(evt: React.ChangeEvent<HTMLInputElement>) {
    setEditMode(evt.target.checked);
  }
  function handleEdgeScrollingChanged(evt: React.ChangeEvent<HTMLInputElement>) {
    localStorage.setItem(`edgeScroll-${params.projectSlug}`, "" + evt.target.checked);
    setEdgeScroll(evt.target.checked);
  }

  function handleCheatModeChanged(evt: React.ChangeEvent<HTMLInputElement>) {
    setCheatMode(evt.target.checked);
  }
  function handleEnableTooltipsChanged(evt: React.ChangeEvent<HTMLInputElement>) {
    const toolTipCheckedValue = evt.target.checked ? "1" : "0";
    localStorage.getItem("display-tooltips-global") !== toolTipCheckedValue
      ? localStorage.setItem("display-tooltips:" + params.projectSlug, toolTipCheckedValue)
      : localStorage.removeItem("display-tooltips:" + params.projectSlug);
    setDisplayTooltips(evt.target.checked);
  }

  let reloadControls = <></>;
  if (localStorage.getItem("user-role")?.includes("admin") || projectRole === "designer") {
    reloadControls = (
      <div className="admin-controls-container">
        <button onClick={restartTable}>Restart table</button>
        <button onClick={reloadDecks}>Reload decks</button>
        <span>{deckReloadStatus}</span>
        <button onClick={reloadDataSheet}>Reload data sheet</button>
        <span>{dataSheetReloadStatus}</span>
        <button onClick={reloadComponents}>Reload components.js (R)</button>
        <button onClick={reloadDevEditorComponents}>Reload dev components from editor</button>
        <button
          onClick={toggleBindTableToEditor}
          style={{ backgroundColor: isEditorBound ? "lightgreen" : "lightgray" }}
        >
          {isEditorBound ? "Unbind this table from editor" : "Bind this table to editor"}
        </button>
      </div>
    );
  }

  return (
    <div className="table-settings-container">
      <Box marginBottom={1}>
        <Stack direction="row" gap="0.25em" alignItems="center">
          <Typography fontSize="1.25rem" fontWeight={501}>
            Zoom
          </Typography>
          <Typography fontSize="0.65rem">
            (<kbd>Ctrl</kbd>
            <kbd>+</kbd>,<kbd>Ctrl</kbd>
            <kbd>-</kbd> or <kbd>Ctrl</kbd>
            <kbd>MouseWheel</kbd>)
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          <Slider
            step={1}
            min={25}
            max={400}
            value={localScaleAdjust}
            valueLabelDisplay="auto"
            onChange={(evt, value) => setLocalScaleAdjust(value as number)}
          />
          <Tooltip title="Reset" arrow>
            <IconButton color="primary" onClick={() => setLocalScaleAdjust(100)}>
              <Replay />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box>
        <Typography fontSize="1.25rem" fontWeight={501}>
          Volume
        </Typography>
        <Stack direction="row" alignItems="center">
          <Slider
            step={0.01}
            min={0}
            max={1}
            value={globalVolume}
            valueLabelDisplay="auto"
            onChange={(evt, value) => changeGlobalVolume(value as number)}
          />
          <Tooltip title="Reset" arrow>
            <IconButton color="primary" onClick={() => changeGlobalVolume(0.5)}>
              <Replay />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box>
        <Typography fontSize="1.25rem" fontWeight={501}>
          Move evaluation speed
        </Typography>
        <Stack direction="row" alignItems="center">
          <Slider
            step={0.05}
            min={0.1}
            max={3}
            value={+evalSpeed}
            valueLabelFormat={(text) => `${text}×`}
            valueLabelDisplay="auto"
            onChange={(evt, value) => changeEvalSpeed(value as number)}
          />
          <Tooltip title="Reset" arrow>
            <IconButton color="primary" onClick={() => changeEvalSpeed(1)}>
              <Replay />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <FormControlLabel control={<Switch checked={editMode} onChange={handleEditModeChanged} />} label={"Edit mode"} />
      <FormControlLabel
        control={<Switch checked={edgeScroll} onChange={handleEdgeScrollingChanged} />}
        label={"Edge scrolling"}
      />
      <FormControlLabel
        control={<Switch checked={displayTooltips} onChange={handleEnableTooltipsChanged} />}
        label={"Display hover tooltips"}
      />
      {reloadControls}
    </div>
  );
}

export default TableSettings;
